/**
 * 全局登录弹框 - 支持微信扫码、账号密码、飞书登录
 */
import React, { useState, useEffect, useRef } from 'react';
import { SparklesIcon, XMarkIcon, UserIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { authApi } from '../services/api';

// 声明微信登录SDK类型
declare global {
  interface Window {
    WxLogin: any;
  }
}

interface LoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultTab?: 'wechat' | 'password' | 'feishu';
}

// 登录方式开关配置
const enableWechatLogin = process.env.REACT_APP_ENABLE_WECHAT_LOGIN === 'true';
const enableFeishuLogin = process.env.REACT_APP_ENABLE_FEISHU_LOGIN === 'true';
const enablePasswordLogin = process.env.REACT_APP_ENABLE_PASSWORD_LOGIN === 'true';

// 计算启用的登录方式数量（用于判断是否显示Tab）
const enabledLoginCount = [enableWechatLogin, enablePasswordLogin].filter(Boolean).length;
const showTabSwitch = enabledLoginCount > 1;

const LoginDialog: React.FC<LoginDialogProps> = ({ isOpen, onClose, onSuccess, defaultTab = 'wechat' }) => {
  // 根据配置决定默认登录方式
  const getDefaultTab = (): 'wechat' | 'password' | 'feishu' => {
    if (enableWechatLogin) return 'wechat';
    if (enablePasswordLogin) return 'password';
    if (enableFeishuLogin) return 'feishu';
    return 'password';
  };
  const [activeTab, setActiveTab] = useState<'wechat' | 'password' | 'feishu'>(getDefaultTab());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [feishuLoading, setFeishuLoading] = useState(false);
  const wechatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLoginForm({ username: '', password: '' });
      setError('');
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  // 初始化微信登录二维码
  useEffect(() => {
    if (isOpen && activeTab === 'wechat' && wechatContainerRef.current && window.WxLogin) {
      wechatContainerRef.current.innerHTML = '';
      
      // 从环境变量获取配置，或使用默认值
      const wxAppId = process.env.REACT_APP_WX_OPEN_APPID || '';
      // 回调到后端接口，在 iframe 内跳转
      const redirectUri = encodeURIComponent(process.env.REACT_APP_WX_REDIRECT_URI || `${window.location.origin}/api/auth/wechat/callback`);
      
      if (wxAppId) {
        new window.WxLogin({
          self_redirect: true,  // 在 iframe 内跳转，不刷新父页面
          id: 'wechat_login_container',
          appid: wxAppId,
          scope: 'snsapi_login',
          redirect_uri: redirectUri,
          state: 'login',
          style: 'black',
          href: 'data:text/css;base64,LmltcG93ZXJCb3ggLnFyY29kZSB7d2lkdGg6IDIwMHB4O30KLmltcG93ZXJCb3ggLnRpdGxlIHtkaXNwbGF5OiBub25lO30KLmltcG93ZXJCb3ggLmluZm8ge3dpZHRoOiAyMDBweDt9Ci5zdGF0dXNfaWNvbiB7ZGlzcGxheTogbm9uZX0KLmltcG93ZXJCb3ggLnN0YXR1cyB7dGV4dC1hbGlnbjogY2VudGVyO30K'
        });
      }
    }
  }, [isOpen, activeTab]);

  // 监听微信登录回调（通过postMessage从loading页面发送）
  useEffect(() => {
    if (!isOpen) return;
    
    const handleMessage = (event: MessageEvent) => {
      // 处理微信登录回调（loading页面发送的code）
      if (event.data && event.data.state === 'login' && event.data.code) {
        handleWechatLogin(event.data.code);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen]);

  // 处理微信登录
  const handleWechatLogin = async (code: string) => {
    try {
      setLoading(true);
      setError('');
      
      // 调用后端接口进行登录
      const response = await authApi.wechatLogin(code);
      console.log('[微信登录] 登录响应:', response.data);
      
      // 检查响应数据结构
      const data = response.data;
      if (data.access_token && data.refresh_token) {
        // 保存token
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        console.log('[微信登录] Token已保存');
        
        // 获取用户信息
        const userResponse = await authApi.getCurrentUser();
        localStorage.setItem('user', JSON.stringify(userResponse.data));
        console.log('[微信登录] 用户信息已保存');
        
        // 触发登录成功事件
        window.dispatchEvent(new CustomEvent('auth:login-success'));
        
        // 登录成功，关闭弹框
        onSuccess();
        onClose();
      } else {
        console.error('[微信登录] 响应数据缺少token:', data);
        setError('登录失败：服务器返回数据异常');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail;
      setError(typeof errorMsg === 'string' ? errorMsg : '微信登录失败，请重试');
      // 重新加载二维码
      if (wechatContainerRef.current) {
        wechatContainerRef.current.innerHTML = '';
        setTimeout(() => {
          if (isOpen && activeTab === 'wechat') {
            const wxAppId = process.env.REACT_APP_WX_OPEN_APPID || '';
            if (wxAppId && window.WxLogin) {
              new window.WxLogin({
                self_redirect: true,
                id: 'wechat_login_container',
                appid: wxAppId,
                scope: 'snsapi_login',
                redirect_uri: encodeURIComponent(process.env.REACT_APP_WX_REDIRECT_URI || `${window.location.origin}/api/auth/wechat/callback`),
                state: 'login',
                style: 'black',
                href: 'data:text/css;base64,LmltcG93ZXJCb3ggLnFyY29kZSB7d2lkdGg6IDIwMHB4O30KLmltcG93ZXJCb3ggLnRpdGxlIHtkaXNwbGF5OiBub25lO30KLmltcG93ZXJCb3ggLmluZm8ge3dpZHRoOiAyMDBweDt9Ci5zdGF0dXNfaWNvbiB7ZGlzcGxheTogbm9uZX0KLmltcG93ZXJCb3ggLnN0YXR1cyB7dGV4dC1hbGlnbjogY2VudGVyO30K'
              });
            }
          }
        }, 100);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authApi.login(loginForm.username, loginForm.password);
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail;
      setError(typeof errorMsg === 'string' ? errorMsg : '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleFeishuLogin = async () => {
    setFeishuLoading(true);
    setError('');
    
    const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    try {
      const response = await authApi.getFeishuAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (err: any) {
      const feishuAuthUrl = `https://open.feishu.cn/open-apis/authen/v1/authorize?client_id=cli_a90fffd185781cd1&redirect_uri=${encodeURIComponent(apiBaseUrl + '/api/auth/feishu/callback')}&state=RANDOMSTRING`;
      window.location.href = feishuAuthUrl;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(15, 30, 60, 0.55)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      />

      {/* 弹框 */}
      <div
        className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden"
        style={{
          background: '#ffffff',
          boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
        }}
      >
        {/* 顶部蓝色装饰条 */}
        <div
          className="px-8 pt-8 pb-6"
          style={{
            background: 'linear-gradient(145deg, #1a3a6b 0%, #1e4d8c 50%, #0e7ab5 100%)',
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white leading-tight">登录 AI标书助手</h2>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  登录后使用完整功能
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg transition-colors flex-shrink-0"
              style={{ color: 'rgba(255,255,255,0.6)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
              }}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab 切换 - 仅在有多种登录方式时显示 */}
        {showTabSwitch && (
        <div className="flex border-b border-gray-200">
          {enableWechatLogin && (
            <button
              onClick={() => setActiveTab('wechat')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'wechat'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.269-.03-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
                </svg>
                微信扫码
              </span>
            </button>
          )}
          {enablePasswordLogin && (
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'password'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <UserIcon className="w-5 h-5" />
                账号密码
              </span>
            </button>
          )}
        </div>
        )}

        {/* 内容区域 */}
        <div className="px-8 py-6">
          {/* 错误提示 */}
          {error && (
            <div
              className="mb-5 px-4 py-3 rounded-xl text-sm"
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
              }}
            >
              {typeof error === 'string' ? error : '发生错误，请重试'}
            </div>
          )}

          {/* 微信扫码登录 - 仅在启用时显示 */}
          {activeTab === 'wechat' && enableWechatLogin && (
            <div className="flex flex-col items-center">
              {process.env.REACT_APP_WX_OPEN_APPID ? (
                <div className="w-full flex flex-col items-center" style={{ height: '260px', paddingTop: '10px' }}>
                  {/* 二维码区域 - 微信JS会自动填充内容（包括标题、二维码、头像、按钮等） */}
                  <div
                    ref={wechatContainerRef}
                    id="wechat_login_container"
                    className="w-full flex items-start justify-center"
                    style={{ height: '250px', maxHeight: '250px', overflow: 'hidden' }}
                  />
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348z"/>
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">微信登录未配置</p>
                  <p className="text-xs text-gray-400 mt-1">请联系管理员配置微信开放平台AppID</p>
                </div>
              )}
            </div>
          )}

          {/* 账号密码登录 - 仅在启用时显示 */}
          {activeTab === 'password' && enablePasswordLogin && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                  用户名
                </label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                    color: '#1e293b',
                  }}
                  placeholder="请输入用户名"
                  required
                  autoFocus
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1e4d8c';
                    e.target.style.background = '#eff6ff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(30,77,140,0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                  密码
                </label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: '#f8fafc',
                    border: '1.5px solid #e2e8f0',
                    color: '#1e293b',
                  }}
                  placeholder="请输入密码"
                  required
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1e4d8c';
                    e.target.style.background = '#eff6ff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(30,77,140,0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
                  style={{
                    background: '#f1f5f9',
                    border: '1.5px solid #e2e8f0',
                    color: '#64748b',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.color = '#475569';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2.5 text-white rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: loading ? '#94a3b8' : 'linear-gradient(135deg, #1e4d8c, #0e7ab5)',
                    boxShadow: loading ? 'none' : '0 4px 16px rgba(30, 77, 140, 0.35)',
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
                >
                  {loading ? '登录中...' : '登录'}
                </button>
              </div>
            </form>
          )}

          {/* 飞书登录 - 独立显示区域 */}
          {activeTab === 'feishu' && enableFeishuLogin && (
            <div className="flex flex-col items-center py-4">
              <div className="text-center mb-6">
                <p className="text-sm" style={{ color: '#64748b' }}>
                  点击下方按钮使用飞书账号登录
                </p>
              </div>
              <button
                type="button"
                onClick={handleFeishuLogin}
                disabled={feishuLoading}
                className="w-full py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: feishuLoading ? '#f1f5f9' : '#ffffff',
                  border: '1.5px solid #e2e8f0',
                  color: feishuLoading ? '#94a3b8' : '#1e293b',
                }}
                onMouseEnter={(e) => {
                  if (!feishuLoading) {
                    e.currentTarget.style.borderColor = '#1e4d8c';
                    e.currentTarget.style.background = '#eff6ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!feishuLoading) {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.background = '#ffffff';
                  }
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 48 48" fill="currentColor">
                  <path d="M20 4H8C5.79086 4 4 5.79086 4 8V20C4 22.2091 5.79086 24 8 24H20C22.2091 24 24 22.2091 24 20V8C24 5.79086 22.2091 4 20 4Z" />
                  <path d="M20 28H8C5.79086 28 4 29.7909 4 32V40C4 42.2091 5.79086 44 8 44H20C22.2091 44 24 42.2091 24 40V32C24 29.7909 22.2091 28 20 28Z" />
                  <path d="M40 4H28C25.7909 4 24 5.79086 24 8V20C24 22.2091 25.7909 24 28 24H40C42.2091 24 44 22.2091 44 20V8C44 5.79086 42.2091 4 40 4Z" />
                  <path d="M40 28H28C25.7909 28 24 29.7909 24 32V40C24 42.2091 25.7909 44 28 44H40C42.2091 44 44 42.2091 44 40V32C44 29.7909 42.2091 28 40 28Z" />
                </svg>
                {feishuLoading ? '跳转中...' : '飞书登录'}
              </button>
            </div>
          )}

          {/* 分隔线和飞书登录 - 在其他登录方式下方显示 */}
          {(activeTab === 'wechat' || activeTab === 'password') && enableFeishuLogin && (
          <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ borderTop: '1px solid #e2e8f0' }}></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-white" style={{ color: '#94a3b8' }}>或使用第三方登录</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleFeishuLogin}
            disabled={feishuLoading}
            className="w-full py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: feishuLoading ? '#f1f5f9' : '#ffffff',
              border: '1.5px solid #e2e8f0',
              color: feishuLoading ? '#94a3b8' : '#1e293b',
            }}
            onMouseEnter={(e) => {
              if (!feishuLoading) {
                e.currentTarget.style.borderColor = '#1e4d8c';
                e.currentTarget.style.background = '#eff6ff';
              }
            }}
            onMouseLeave={(e) => {
              if (!feishuLoading) {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = '#ffffff';
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="61" height="20" fill="none" viewBox="0 0 61 20"><g clip-path="url(#feishu_svg__a)"><path fill="currentColor" d="M42.733 13.933c-.499.898-1.087 1.432-1.596 1.432-2.455 0-3.016-5.345-3.016-8.181V3.8a.676.676 0 0 0-.676-.67h-8.129v1.496a.566.566 0 0 0 .566.566h6.169V7.19c0 3.829.875 10.244 5.078 10.244 1.038 0 1.996-.551 2.804-1.586a.566.566 0 0 0-.047-.749z"></path><path fill="currentColor" d="m39.211 9.564 2.744 2.731a.806.806 0 0 0 1.142 0l.823-.82-3.315-3.303 1.644-1.644a.806.806 0 0 0 0-1.142l-.82-.824-2.218 2.215-.778.781a.87.87 0 0 0 0 1.227zM60.136 6.665 58.42 3.692a1.13 1.13 0 0 0-.98-.561h-1.434a.112.112 0 0 0-.098.17l.913 1.576h-4.026V2.632h-2.177v2.245h-3.894V6.28a.566.566 0 0 0 .566.566h3.328v2.667h-5.024v1.407a.564.564 0 0 0 .564.563h4.46v5.944h2.177v-5.944h4.795l-.055 1.584a.903.903 0 0 1-.9.87h-2.173v1.415a.566.566 0 0 0 .566.566h2.14a2.417 2.417 0 0 0 2.415-2.332l.117-3.362a.68.68 0 0 0-.676-.701h-1.676l.124-3.525.16.28a1.13 1.13 0 0 0 .98.566h1.427a.112.112 0 0 0 .097-.18m-4.854 2.849h-2.494v-2.68h2.582z"></path><path fill="#133C9A" d="M20.608 12.253v.012l.048-.09z"></path><path fill="#00D6B9" d="m12.31 10.474.059-.058.119-.114.08-.08.247-.25.33-.326.273-.28.265-.25.278-.273.248-.25.352-.352.205-.194c.127-.117.248-.232.395-.34.146-.107.248-.196.381-.289q.273-.198.563-.366a7.5 7.5 0 0 1 1.48-.684l.166-.055a14 14 0 0 0-2.63-5.31 1.27 1.27 0 0 0-.992-.477H3.964a.19.19 0 0 0-.18.133.19.19 0 0 0 .069.214 29.8 29.8 0 0 1 8.427 9.623z"></path><path fill="#3370FF" d="M8.28 19.473c2.49 0 4.934-.668 7.08-1.938a14 14 0 0 0 5.123-5.283l.248-.456q-.178.343-.399.66-.079.112-.161.22a3 3 0 0 1-.213.25c-.075.074-.117.13-.176.194a5.3 5.3 0 0 1-.883.746c-.094.062-.119.08-.18.117l-.192.112a5 5 0 0 1-.404.205c-.139.062-.248.102-.362.147a5.2 5.2 0 0 1-1.49.32 3 3 0 0 1-.496 0c-.168-.016-.364-.016-.55-.036a6.418 6.418 0 0 1-.766-.135l-.189-.047-.523-.147-.248-.077-.386-.117-.338-.078-.297-.1-.198-.07-.248-.084-.176-.065a10 10 0 0 1-.345-.127l-.2-.075-.266-.105-.283-.112-.18-.075-.226-.095-.174-.074-.18-.078-.157-.07-.144-.065-.144-.064-.148-.07-.189-.088-.188-.104-.21-.102-.18-.088A29.3 29.3 0 0 1 .33 6.183a.19.19 0 0 0-.33.132v9.753a1.27 1.0 0 0 0 .56 1.058 13.83 13.83 0 0 0 7.716 2.34"></path><path fill="#133C9A" d="M23.684 6.757a7.98 7.98 0 0 0-5.702-.524l-.248.073-.167.054a7.5 7.5 0 0 0-.9.37c-.185.09-.391.202-.58.314a6 6 0 0 0-.562.367c-.132.092-.248.187-.382.289a6 6 0 0 0-.394.34l-.206.194-.33.351-.248.25-.282.282-.266.25-.28.281-.33.327-.248.25-.079.079-.119.115-.06.057-.089.085-.099.092a14 14 0 0 1-2.893 2.063l.188.087.15.07.143.065.144.065.156.07.18.077.175.075.225.095.181.075.283.112.265.104.2.075q.173.068.345.128l.176.064.248.085.204.07.297.1.315.1.387.117.248.077.523.147.188.048q.18.045.357.075.18.03.41.06.278.03.55.034c.18.003.332 0 .495 0 .164 0 .31-.03.464-.052a6 6 0 0 0 .622-.135 6 6 0 0 0 .767-.28c.114-.051.272-.129.404-.204l.19-.112q.093-.056.181-.117c.06-.04.186-.13.276-.197.089-.068.15-.122.225-.185.132-.114.248-.25.382-.364.06-.065.12-.127.176-.194.057-.068.144-.173.213-.25.07-.077.11-.145.162-.22q.218-.316.396-.655l.134-.275 1.24-2.462a7.8 7.8 0 0 1 1.599-2.332"></path></g><defs><clipPath id="feishu_svg__a"><path fill="#fff" d="M0 .526h60.159v18.947H0z"></path></clipPath></defs></svg>
            {feishuLoading ? '跳转中...' : '飞书登录'}
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginDialog;
